import { FileUpload, type FileUploadSelectEvent } from "primereact/fileupload";
import { useRef, useState } from "react";
import { showToast } from "../../../../../shared/global-tools/toast-system";
import {
  type ParsedData,
  parseCsvFile,
  parseExcelFile,
  parseJsonFile,
} from "../../../../../shared/parse-imported-file";
import Icon from "../../../../utils/Icons/Icon";
import "./index.scss";

interface DropFileZoneProps {
  importedData?: ImportFileResult;
  onImport(data?: ImportFileResult): void;
}

export interface ImportFileResult {
  file: File;
  parsedData?: ParsedData;
}

export default function DropFileZone({ importedData, onImport }: DropFileZoneProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const fileUploadRef = useRef<FileUpload>(null);

  const handleFileSelect = async (event: FileUploadSelectEvent) => {
    const file = event.files[0];
    if (!file) return;
    if (file.type !== "text/csv") {
      showToast("Seul le format CSV est supporté", { severity: "warn" });
      onImport(undefined);
      setError(undefined);
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
      return;
    }
    setIsLoading(true);
    setError(undefined);

    try {
      let parsedData:
        | {
            data: Record<string, unknown>[];
            objectKeys: string[];
          }
        | undefined;
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        parsedData = await parseCsvFile(file);
      } else if (file.type === "application/json" || file.name.endsWith(".json")) {
        parsedData = await parseJsonFile(file);
      } else if (
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls")
      ) {
        parsedData = await parseExcelFile(file);
      }
      onImport({ file, parsedData });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'import du fichier");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileRemove = () => {
    onImport(undefined);
    setError(undefined);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  const data = importedData?.parsedData?.data || [];
  return (
    <div className="DropFileZone">
      <div className="DropFileZone-container">
        <h3 className="DropFileZone-title">Téléverser le fichier</h3>

        <div className="DropFileZone-upload">
          {data.length > 0 ? (
            <div className="DropFileZone-success">
              <div className="DropFileZone-successIcon">
                <Icon name="check" width={24} height={24} color="#4CAF50" />
              </div>
              <p className="DropFileZone-successText">
                Fichier importé avec succès ({data.length} entrées)
              </p>
              <button type="button" className="DropFileZone-removeBtn" onClick={handleFileRemove}>
                Supprimer le fichier
              </button>
            </div>
          ) : (
            <FileUpload
              ref={fileUploadRef}
              mode="basic"
              name="importFile"
              accept=".csv"
              maxFileSize={10485760} // 10MB
              onSelect={handleFileSelect}
              auto={false}
              customUpload={true}
              disabled={isLoading}
              className="DropFileZone-fileUpload"
              chooseOptions={{
                className: "DropFileZone-uploadBtn",
                label: isLoading ? "Traitement..." : "Sélectionner un fichier",
              }}
            />
          )}

          {!importedData?.file && (
            <div className="DropFileZone-content">
              <div className="DropFileZone-icon">
                <Icon name="download" width={48} height={48} color="#6c757d" />
              </div>
              <p className="DropFileZone-text">Glissez-déposez votre fichier ici</p>
              <p className="DropFileZone-hint">Format supporté: CSV (Encodage UTF-8)</p>
            </div>
          )}

          {error && (
            <div className="DropFileZone-error">
              <p>{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
